import type {
  AppSchemasCustomerAddress,
  Branch,
  BranchConfig,
} from "@/api/online-ordering-service/model";

export type FulfillmentMode = "delivery" | "pickup";

type MaybeLocalized = Record<string, unknown> | string | null | undefined;

export function localizeValue(value: MaybeLocalized, lang: string): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  const direct = value[lang];
  if (typeof direct === "string" && direct) return direct;
  if (typeof value.en === "string" && value.en) return value.en;
  if (typeof value.ar === "string" && value.ar) return value.ar;
  for (const candidate of Object.values(value)) {
    if (typeof candidate === "string" && candidate) return candidate;
  }
  return "";
}

export function branchIdentifier(branch: BranchConfig | Branch | null | undefined): string {
  if (!branch) return "";
  return (
    (branch as BranchConfig).branchId ??
    (branch as Branch).id ??
    ""
  );
}

export function branchDisplayName(
  branch: BranchConfig | Branch | null | undefined,
  lang: string,
): string {
  if (!branch) return "";
  return (
    localizeValue((branch as BranchConfig).branchName, lang) ||
    localizeValue((branch as Branch).name, lang) ||
    (branch as Branch).areaName ||
    branchIdentifier(branch)
  );
}

export function addressDisplayLine(
  address: AppSchemasCustomerAddress | null | undefined,
): string {
  if (!address) return "";
  return [address.street, address.street2].filter(Boolean).join(", ") || address.city;
}

export function branchAddressLine(
  branch: BranchConfig | Branch | null | undefined,
  lang: string,
): string {
  const address = (branch as { address?: Record<string, unknown> | null } | null | undefined)
    ?.address;
  if (!address) return "";

  const line =
    localizeValue(address.line1 as MaybeLocalized, lang) ||
    localizeValue(address.street as MaybeLocalized, lang) ||
    localizeValue(address.name as MaybeLocalized, lang);
  const line2 =
    localizeValue(address.line2 as MaybeLocalized, lang) ||
    localizeValue(address.street2 as MaybeLocalized, lang);
  const city = localizeValue(address.city as MaybeLocalized, lang);
  const area = localizeValue(address.area as MaybeLocalized, lang);
  const state = localizeValue(address.state as MaybeLocalized, lang);

  return [line, line2, area || city, state].filter(Boolean).join(", ");
}

export function branchSupports(
  branch: BranchConfig | Branch | null | undefined,
  mode: FulfillmentMode,
): boolean {
  if (!branch) return false;
  const record = branch as unknown as Record<string, unknown>;
  const field = mode === "delivery" ? "supportsDelivery" : "supportsPickup";
  const value = record[field];
  return typeof value === "boolean" ? value : true;
}

export function pricingAmount(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value && typeof value === "object") {
    const amount = (value as { amount?: unknown }).amount;
    if (typeof amount === "number") return Number.isFinite(amount) ? amount : 0;
    if (typeof amount === "string") {
      const parsed = Number(amount);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }
  return 0;
}
